import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, MapPin, PenLine, CheckCircle2, Navigation, X, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

function SignaturePad({ onSave, onCancel }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  const pos = (e) => {
    const c = ref.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0A1428"; ctx.lineWidth = 2; ctx.stroke(); setEmpty(false); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setEmpty(true); };

  return (
    <div>
      <canvas ref={ref} width={480} height={180} data-testid="signature-canvas"
        className="w-full border border-navy/15 rounded-lg bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div className="flex gap-2 mt-2">
        <button onClick={clear} className="btn btn-ghost text-sm"><Eraser className="w-4 h-4" /> Limpiar</button>
        <button onClick={onCancel} className="btn btn-ghost text-sm ml-auto">Cancelar</button>
        <button onClick={() => onSave(ref.current.toDataURL("image/png"))} disabled={empty} className="btn btn-primary text-sm" data-testid="signature-save"><CheckCircle2 className="w-4 h-4" /> Firmar y cerrar</button>
      </div>
    </div>
  );
}

export default function FieldMyDay() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);   // ticket being closed
  const [nombre, setNombre] = useState("");

  const load = useCallback(() => { setLoading(true); api.get("/field/my-tickets").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const close = async (dataUrl) => {
    try {
      await api.post(`/field/tickets/${signing.id}/close`, { firma: dataUrl, cliente_nombre: nombre });
      toast.success(`Ticket ${signing.folio || ""} cerrado con firma`);
      setSigning(null); setNombre(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  if (loading) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const abiertos = data?.abiertos || [];

  return (
    <div data-testid="field-myday" className="max-w-2xl">
      <div className="mb-4 text-sm text-navy/50">Técnico: <b className="text-navy/80">{data?.tecnico || "—"}</b> · {abiertos.length} ticket(s) abierto(s)</div>
      {abiertos.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-navy/40">No tienes tickets asignados. Los tickets aparecen aquí cuando se te asignan en el <b>Despacho</b>.</div>
      ) : (
        <div className="space-y-3">
          {abiertos.map((t) => (
            <div key={t.id} className="card-surface p-4" data-testid={`myday-ticket-${t.folio || t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{t.folio || "Ticket"} · {t.empresa}</div>
                  <div className="text-xs text-navy/45 mt-0.5">{t.tipo} · {t.direccion}{t.ciudad ? `, ${t.ciudad}` : ""}</div>
                  <div className="text-xs text-navy/50 mt-1">Checklist: {t.checklist_done}/{t.checklist_total}</div>
                </div>
                {t.lat != null && (
                  <a href={`https://www.openstreetmap.org/?mlat=${t.lat}&mlon=${t.lng}#map=17/${t.lat}/${t.lng}`} target="_blank" rel="noreferrer"
                    className="btn btn-ghost text-sm shrink-0" data-testid={`myday-nav-${t.folio || t.id}`}><Navigation className="w-4 h-4" /> Ir</a>
                )}
              </div>
              <button onClick={() => { setSigning(t); setNombre(""); }} className="btn btn-primary text-sm w-full mt-3" data-testid={`myday-close-${t.folio || t.id}`}>
                <PenLine className="w-4 h-4" /> Cerrar con firma del cliente
              </button>
            </div>
          ))}
        </div>
      )}

      {signing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="signature-modal">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setSigning(null)} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Firma del cliente · {signing.folio}</h3>
              <button onClick={() => setSigning(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre de quien recibe" className="field-sm w-full mb-3" data-testid="signature-name" />
            <SignaturePad onSave={close} onCancel={() => setSigning(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
